import { useState, useEffect } from 'react';
import type { AssetStatus, AssetCategory } from '@/types/asset';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Filter, X, Loader2, AlertCircle } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { dataService } from '@/services/data-service';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface AssetFiltersProps {
  filters: {
    status?: AssetStatus[];
    category?: AssetCategory[];
    location?: string[];
  };
  onFiltersChange: (filters: AssetFiltersProps['filters']) => void;
}

const statusOptions: AssetStatus[] = ['active', 'inactive', 'maintenance', 'disposed', 'pending'];

export function AssetFilters({ filters, onFiltersChange }: AssetFiltersProps): React.ReactElement {
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);

  useEffect(() => {
    async function loadFilters() {
      try {
        setLoadingCategories(true);
        setLoadingLocations(true);
        setCategoryError(null);
        setLocationError(null);
        
        // Get all assets to extract unique categories and locations
        const assetsData = await dataService.getAssets({ pageSize: 1000 });
        
        // Extract unique categories
        const uniqueCategories = Array.from(
          new Set(assetsData.data.map((asset: any) => asset.category))
        ).sort() as AssetCategory[];
        
        // Extract unique locations
        const uniqueLocations = Array.from(
          new Set(assetsData.data.map((asset: any) => asset.location))
        ).sort() as string[];
        
        setCategories(uniqueCategories);
        setLocations(uniqueLocations);
      } catch (err) {
        console.error('Failed to load filter options:', err);
        setCategoryError('Failed to load categories');
        setLocationError('Failed to load locations');
      } finally {
        setLoadingCategories(false);
        setLoadingLocations(false);
      }
    }
    loadFilters();
  }, []);

  const activeFilterCount =
    (filters.status?.length || 0) +
    (filters.category?.length || 0) +
    (filters.location?.length || 0);

  const handleStatusChange = (status: AssetStatus, checked: boolean) => {
    const current = filters.status || [];
    const updated = checked
      ? [...current, status]
      : current.filter((s) => s !== status);
    onFiltersChange({ ...filters, status: updated.length > 0 ? updated : undefined });
  };

  const handleCategoryChange = (category: AssetCategory, checked: boolean) => {
    const current = filters.category || [];
    const updated = checked
      ? [...current, category]
      : current.filter((c) => c !== category);
    onFiltersChange({ ...filters, category: updated.length > 0 ? updated : undefined });
  };

  const handleLocationChange = (location: string, checked: boolean) => {
    const current = filters.location || [];
    const updated = checked
      ? [...current, location]
      : current.filter((l) => l !== location);
    onFiltersChange({ ...filters, location: updated.length > 0 ? updated : undefined });
  };

  const clearFilters = () => {
    onFiltersChange({});
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Filter className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && ` (${activeFilterCount})`}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="start">
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          <div className="flex items-center justify-between sticky top-0 bg-popover z-10 pb-2 -mx-2 px-2">
            <h4 className="font-semibold">Filters</h4>
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-auto p-0 text-xs"
              >
                Clear all
              </Button>
            )}
          </div>

          <Separator />

          {/* Status Filter */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Status</Label>
            <div className="space-y-2">
              {statusOptions.map((status) => (
                <div key={status} className="flex items-center space-x-2">
                  <Checkbox
                    id={`status-${status}`}
                    checked={filters.status?.includes(status)}
                    onCheckedChange={(checked) =>
                      handleStatusChange(status, checked as boolean)
                    }
                  />
                  <label
                    htmlFor={`status-${status}`}
                    className="text-sm capitalize leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {status}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Category Filter */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Category</Label>
            {loadingCategories ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading categories...
              </div>
            ) : categoryError ? (
              <Alert variant="destructive" className="py-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">{categoryError}</AlertDescription>
              </Alert>
            ) : categories.length === 0 ? (
              <p className="text-sm text-muted-foreground">No categories available</p>
            ) : (
              <div className="space-y-2">
                {categories.map((category) => (
                  <div key={category} className="flex items-center space-x-2">
                    <Checkbox
                      id={`category-${category}`}
                      checked={filters.category?.includes(category)}
                      onCheckedChange={(checked) =>
                        handleCategoryChange(category, checked as boolean)
                      }
                    />
                    <label
                      htmlFor={`category-${category}`}
                      className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {category}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Separator />

          {/* Location Filter */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Location</Label>
            {loadingLocations ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading locations...
              </div>
            ) : locationError ? (
              <Alert variant="destructive" className="py-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">{locationError}</AlertDescription>
              </Alert>
            ) : locations.length === 0 ? (
              <p className="text-sm text-muted-foreground">No locations available</p>
            ) : (
              <div className="space-y-2">
                {locations.map((location) => (
                  <div key={location} className="flex items-center space-x-2">
                    <Checkbox
                      id={`location-${location}`}
                      checked={filters.location?.includes(location)}
                      onCheckedChange={(checked) =>
                        handleLocationChange(location, checked as boolean)
                      }
                    />
                    <label
                      htmlFor={`location-${location}`}
                      className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {location}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
